import { extractQueryFromUrl, extractLocationFromUrl } from "./util/urlParsingUtils";
import { Box } from "../../../../../mads";
import { JobAlertSignupToggle } from "../../../../../shared/components/JobAlertSignup";
import { useSubscriptionMatchQuery } from "../../../../../shared/components/JobAlertSignup/graphql/__generated__/subscriptionMatch.gqlgenerated";
import { useTareData } from "../../../../../shared/native-modules/tare/TareDataContext";
import { CookieKey } from "../../../../../shared/native-modules/types/RNCoreNativeModule";
import { useCookies } from "../../../../../shared/util/CookieProvider";
import { navigateView } from "../../../../native-module/navigateView";
import {
  useViewJobDataContext,
  useViewjobOneGraphModel,
  useViewjobSourceUrl,
} from "../../../context/ViewJobDataContext";
import { ViewJobDivider } from "../Divider";

import { spacing } from "@indeed/ifl-tokens/react-native";
import React, { useCallback } from "react";

/**
 * Parse the MCLK (click tracking) cookie value to extract search query and location.
 * The MCLK value is a URL-encoded query string set by jobCardDidTap on the native side
 * (e.g., "jk=xxx&what=software+engineer&where=Austin").
 */
function parseSearchParamsFromMclk(mclk: string | null | undefined): {
  what: string;
  where: string;
} {
  if (!mclk) return { what: "", where: "" };
  try {
    const params = new URLSearchParams(mclk);
    return {
      what: decodeURIComponent(params.get("what") ?? ""),
      where: decodeURIComponent(params.get("where") ?? ""),
    };
  } catch {
    return { what: "", where: "" };
  }
}

interface InnerProps {
  queryWhat: string;
  queryWhere: string;
  country?: string;
  language?: string;
  navigateToUrl: (url: string) => void;
}

function ViewJobJobAlertSignupInner({
  queryWhat,
  queryWhere,
  country,
  language,
  navigateToUrl,
}: InnerProps): React.JSX.Element | null {
  const { data, loading } = useSubscriptionMatchQuery({
    variables: {
      input: {
        filter: {
          query: queryWhat,
          location: queryWhere,
        },
        subscriptionStates: ["ACTIVE", "UNCONF"],
      },
    },
    fetchPolicy: "cache-first",
  });

  if (loading) {
    return null;
  }

  const existingSubscriptionState = data?.jobAlert?.subscriptionMatch?.subscriptionState ?? null;

  return (
    <Box>
      <ViewJobDivider sx={{ marginTop: spacing.s4, marginBottom: spacing.s4 }} />
      <JobAlertSignupToggle
        queryWhat={queryWhat}
        queryWhere={queryWhere}
        country={country}
        language={language}
        pageName="viewjob"
        existingSubscriptionState={existingSubscriptionState}
        navigateToUrl={navigateToUrl}
      />
      <ViewJobDivider sx={{ marginTop: spacing.s4 }} />
    </Box>
  );
}

export function ViewJobJobAlertSignup(): React.JSX.Element | null {
  const viewJobDataContext = useViewJobDataContext();
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const vjSourceUrl = useViewjobSourceUrl();
  const initialData = viewJobDataContext.initialData;
  const cookies = useCookies();
  const tareData = useTareData();
  const userEmail = tareData?.accountStatus?.emailAddress;

  const handleNavigateToUrl = useCallback(
    (url: string): void => {
      navigateView(vjSourceUrl, url, url, undefined).catch((error) => {
        console.error("Failed to navigate:", error);
      });
    },
    [vjSourceUrl],
  );

  if (!viewjobOneGraphModel || !userEmail) {
    return null;
  }

  // Try multiple sources for the search query
  const destUrl = initialData?.jobAlertSignUp?.alertFormData?.dest ?? "";
  let queryWhat = "";
  let queryWhere = "";

  const queryFromFormData = initialData?.jobAlertSignUp?.alertFormData?.q;
  const locationFromFormData = initialData?.jobAlertSignUp?.alertFormData?.l;

  // Fallback: read search context directly from the MCLK cookie.
  // When navigating from RN SERP, jobCardDidTap sets the MCLK cookie with
  // the search what/where, but the backend may not populate alertFormData
  // (e.g., no prevUrl in the ViewJob URL, or on the OneGraph path).
  const mclkParams = parseSearchParamsFromMclk(cookies[CookieKey.clickTrackingLog]);

  // Extract query: alertFormData > dest URL > MCLK cookie > VJ job title
  queryWhat =
    queryFromFormData ||
    (destUrl && extractQueryFromUrl(destUrl)) ||
    mclkParams.what ||
    viewjobOneGraphModel.job?.jobTitle ||
    "";

  // Get location - prefer original search location from URL, then formData
  const locationFromUrl = destUrl ? extractLocationFromUrl(destUrl) : "";

  // Priority: URL (original search) > formData > MCLK cookie > job location
  queryWhere =
    locationFromUrl ||
    locationFromFormData ||
    mclkParams.where ||
    viewjobOneGraphModel.job?.formattedLocation ||
    "";

  return (
    <ViewJobJobAlertSignupInner
      queryWhat={queryWhat}
      queryWhere={queryWhere}
      country={initialData?.country}
      language={initialData?.language}
      navigateToUrl={handleNavigateToUrl}
    />
  );
}
